import type { CSSProperties } from 'react'
import React from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AddButton, Dropdown, Icon, PriceString, Tag } from '@fourthwall/components'
import { CogIcon, DragIcon } from '@fourthwall/icons'
import { pluralize } from '@fourthwall/utils/dist/string'
import { reverse, routing } from '@utils/routing'

import { usePerksContext } from '../../hooks/usePerksContext'
import { useTiersContext } from '../../hooks/useTiersContext'
import type { FreeTier, Tier as TierType } from '../../types'
import { isPaidTier } from '../../utils'
import { Perks } from '../Perks'
import * as S from './styled'

type Props = {
  tier: TierType | FreeTier
}

export const TierBox = ({ tier }: Props) => {
  const { deleteFreeTier, deleteTier, publishTier, unpublishTier } = useTiersContext()
  const { addPerk } = usePerksContext()

  const { isDragging, setNodeRef, transform, attributes, listeners } = useSortable({
    id: tier.id,
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 999 : undefined,
  }

  const renderStatus = () => {
    if (tier.status === 'Unpublished') {
      return <Tag appearance="default" label="Unpublished" />
    }

    if (tier.status === 'Published') {
      return <Tag appearance="success" label="Published" />
    }
  }

  const renderPrice = () => {
    if (isPaidTier(tier)) {
      return <PriceString value={tier.variants.monthly.amounts.usd.amount / 100} />
    }

    return 'Free'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <S.Container data-testid={`Tier.container_${tier.name}`}>
        <S.TopContainer>
          {renderStatus()}
          <S.Options data-testid="Tier.options">
            <Dropdown
              content={
                <>
                  <Dropdown.Item
                    label="Edit tier"
                    to={reverse(routing.memberships.perks.tier.self, {
                      tierId: isPaidTier(tier) ? tier.id : 'free',
                    })}
                  />
                  <Dropdown.Item
                    label="Delete tier"
                    onClick={() => {
                      isPaidTier(tier) ? deleteTier(tier) : deleteFreeTier()
                    }}
                  />
                  {isPaidTier(tier) && (
                    <Dropdown.Item
                      label={tier.status === 'Unpublished' ? 'Publish tier' : 'Unpublish tier'}
                      onClick={() => {
                        tier.status === 'Unpublished' ? publishTier(tier) : unpublishTier(tier)
                      }}
                    />
                  )}
                </>
              }
            >
              <S.Option type="button" data-testid="Tier.OptionsIcon">
                <Icon component={CogIcon} height={12} />
              </S.Option>
            </Dropdown>

            <S.Option type="button" data-testid="Tier.DragIcon" {...listeners} {...attributes}>
              <Icon component={DragIcon} height={12} />
            </S.Option>
          </S.Options>
        </S.TopContainer>

        <S.InfoContainer>
          {tier.image?.url ? <S.Image src={tier.image.url} /> : null}
          <S.Name data-testid="Tier.name">{tier.name}</S.Name>

          <S.Price data-testid="Tier.price">{renderPrice()}</S.Price>

          {tier.description ? <S.Description>{tier.description}</S.Description> : null}

          <S.MembersCount data-testid="Tier.members">
            {tier.statistics.membersCount} {pluralize(tier.statistics.membersCount, 'member')}
          </S.MembersCount>
        </S.InfoContainer>

        <S.PerksContainer data-testid="Tier.perksContainer">
          <Perks />
          {addPerk && (
            <S.AddPerkWrapper>
              <AddButton label="Add perk" onClick={() => addPerk(tier.id)} />
            </S.AddPerkWrapper>
          )}
        </S.PerksContainer>
      </S.Container>
    </div>
  )
}
